'use client';
import {
  QueryClient,
  QueryClientProvider,
  useQueryClient,
} from '@tanstack/react-query';
import dynamic from 'next/dynamic';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { useSessionRevival } from '@/hooks/use-session-revival';
import { useTeamMembersRealtimeSync } from '@/hooks/useTeamMembersRealtimeSync';

import { ChunkLoadBoundary } from '@/components/chunk-load-boundary';
import { TabletFocusGuard } from '@/components/layout/TabletFocusGuard';
import { PostHogProvider } from '@/components/posthog/posthog-provider';
import { SentryProvider } from '@/components/sentry/sentry-provider';
import { SubscriptionDialogProvider } from '@/components/subscription/SubscriptionDialogProvider';
import { SubscriptionEnforcer } from '@/components/subscription/SubscriptionEnforcer';
import { TourProvider } from '@/components/tour';
import { TourManager } from '@/components/tour/TourManager';
import { TooltipProvider } from '@/components/ui/tooltip';

import {
  hasRecoveryBeenAttempted,
  isChunkLoadError,
  recoverFromStaleChunks,
} from '@/lib/chunk-load-recovery';
import { createSupabaseBrowserClient } from '@/lib/supabase/client';
import { getTenantConfigFromSession } from '@/lib/tenant/session';

import { paths } from '@/constants/paths';
import { QueryKeys } from '@/constants/query-keys';

/** Loaded on demand — keeps POS/calendar code out of every edge function chunk. */
const AppointmentSheet = dynamic(
  () =>
    import('@/components/calendar/appointment-sheet/AppointmentSheet').then(
      (mod) => mod.AppointmentSheet,
    ),
  { ssr: false },
);

const CheckoutSheet = dynamic(
  () =>
    import('@/components/checkout/CheckoutSheet').then(
      (mod) => mod.CheckoutSheet,
    ),
  { ssr: false },
);

const ReactQueryDevtools =
  process.env.NODE_ENV === 'development'
    ? dynamic(
        () =>
          import('@tanstack/react-query-devtools').then((mod) => ({
            default: mod.ReactQueryDevtools,
          })),
        { ssr: false },
      )
    : () => null;

type AppProviderProps = {
  children: React.ReactNode;
};

/**
 * Mounts once inside QueryClientProvider.
 * - useSessionRevival: forces token refresh on tab-visible after device sleep
 * - onAuthStateChange: keeps React Query USER cache in sync with Supabase auth events
 */
/** CalendarHeader owns the zoom slider, but only mounts on the calendar page — apply the saved scale here so it still takes effect on a fresh load of any other page. */
function FontScaleSync() {
  useEffect(() => {
    const scale = Number(localStorage.getItem('font-scale')) || 1;
    document.documentElement.style.fontSize = `${16 * scale}px`;
  }, []);

  return null;
}

function AuthSync() {
  useSessionRevival();
  const qc = useQueryClient();

  useEffect(() => {
    const tenantConfig = getTenantConfigFromSession();
    if (!tenantConfig?.supabase_url || !tenantConfig?.supabase_anon_key) return;

    const supabase = createSupabaseBrowserClient();
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'TOKEN_REFRESHED' || event === 'SIGNED_IN') {
        // Push refreshed user into cache — no extra network call needed
        qc.setQueryData([QueryKeys.USER], session?.user ?? null);
      }
      if (event === 'SIGNED_OUT') {
        qc.clear();
        // Shared cart pages are public — never force a redirect to login from here
        if (
          window.location.pathname !== paths.auth.login &&
          !window.location.pathname.startsWith('/cart/')
        ) {
          // Clear tenant_id before navigating so middleware serves login directly
          document.cookie = 'tenant_id=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';
          window.location.href = paths.auth.login;
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [qc]);

  return null;
}

function TeamMembersSync() {
  useTeamMembersRealtimeSync();
  return null;
}

function GlobalChunkErrorHandler() {
  const recoveredRef = useRef(false);

  const handleRejection = useCallback((event: PromiseRejectionEvent) => {
    if (recoveredRef.current) return;
    if (!isChunkLoadError(event.reason)) return;

    event.preventDefault();

    if (hasRecoveryBeenAttempted()) return;

    recoveredRef.current = true;
    recoverFromStaleChunks();
  }, []);

  useEffect(() => {
    window.addEventListener('unhandledrejection', handleRejection);
    return () => window.removeEventListener('unhandledrejection', handleRejection);
  }, [handleRejection]);

  return null;
}

export default function AppProviders({ children }: AppProviderProps) {
  const queryClient = useMemo(() => new QueryClient(), []);

  return (
    <ChunkLoadBoundary>
      <GlobalChunkErrorHandler />
    <QueryClientProvider client={queryClient}>
      <TourProvider>
        <AuthSync />
        <FontScaleSync />
        <TeamMembersSync />
         <SubscriptionEnforcer />
        <SubscriptionDialogProvider>
        <TabletFocusGuard />
        <TooltipProvider delayDuration={0}>
          <PostHogProvider>
            <SentryProvider>{children}</SentryProvider>
          </PostHogProvider>
          <AppointmentSheet />
          <CheckoutSheet />
        </TooltipProvider>
        <TourManager />
        <ReactQueryDevtools />
       </SubscriptionDialogProvider>
      </TourProvider>
    </QueryClientProvider>
    </ChunkLoadBoundary>
  );
}
